import { useRef, useEffect } from 'react'

/**
 * A hook that wraps a callback function in a debouncer.
 *
 * @param {function} callback - The function to be called after the debouncing.
 * @param {number} delay - The time to wait before calling the callback.
 * @param {object} options - An object with options to customize the debouncer.
 * @param {boolean} options.leading - Whether to call the callback at the start of the delay.
 * @param {boolean} options.trailing - Whether to call the callback at the end of the delay.
 *
 * @returns {function} A debounced version of the callback function.
 */
function useDebounce(callback, delay, options = {}) {
  const { leading = false, trailing = true } = options;
  const timer = useRef();

  useEffect(() => (
    () => {
      clearTimeout(timer.current);
    }
  ), []);

  return (...args) => {
    const callNow = leading && !timer.current;
    clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      if (trailing && !callNow) {
        callback(...args);
      }
      timer.current = null;
    }, delay);

    if (callNow) {
      callback(...args);
    }
  };
}

export default useDebounce
